import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function OrderTicket({ symbol, quote, onSubmitted }) {
  const [side, setSide] = useState("buy");
  const [qty, setQty] = useState(1);
  const [orderType, setOrderType] = useState("market");
  const [limitPrice, setLimitPrice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (quote?.price) setLimitPrice(quote.price.toFixed(2));
  }, [quote?.price, symbol]);

  const est = (Number(orderType === "limit" ? limitPrice : quote?.price) || 0) * Number(qty || 0);

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        symbol,
        side,
        quantity: Number(qty),
        order_type: orderType,
        limit_price: orderType === "limit" ? Number(limitPrice) : null,
      };
      const { data } = await api.post("/orders", body);
      toast.success(`${side.toUpperCase()} ${data.quantity} ${data.symbol} @ $${data.price}`);
      onSubmitted?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Order failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-3 border-b border-zinc-800">
        <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Order Ticket</div>
        <div className="text-sm text-zinc-300 mono mt-1">{symbol}</div>
      </div>

      <div className="p-5 space-y-4 flex-1">
        <Tabs value={side} onValueChange={setSide} className="w-full">
          <TabsList className="grid grid-cols-2 w-full bg-zinc-950 border border-zinc-800 rounded-sm p-0 h-10">
            <TabsTrigger
              value="buy"
              data-testid="order-side-buy"
              className="rounded-sm data-[state=active]:bg-[#00C805] data-[state=active]:text-black text-zinc-400 font-semibold"
            >BUY</TabsTrigger>
            <TabsTrigger
              value="sell"
              data-testid="order-side-sell"
              className="rounded-sm data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white text-zinc-400 font-semibold"
            >SELL</TabsTrigger>
          </TabsList>
        </Tabs>

        <div>
          <Label className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Order type</Label>
          <div className="grid grid-cols-2 gap-1 mt-2">
            {["market", "limit"].map((t) => (
              <button
                key={t}
                data-testid={`order-type-${t}`}
                onClick={() => setOrderType(t)}
                className={`h-9 border text-xs mono uppercase tracking-widest rounded-sm ${
                  orderType === t
                    ? "border-white bg-zinc-800 text-white"
                    : "border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-600"
                }`}
              >{t}</button>
            ))}
          </div>
        </div>

        <div>
          <Label htmlFor="qty" className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Quantity</Label>
          <Input
            id="qty" type="number" step="1" min="1" value={qty}
            onChange={(e) => setQty(e.target.value)}
            data-testid="order-quantity-input"
            className="bg-zinc-950 border-zinc-700 rounded-sm mono mt-2 h-10"
          />
        </div>

        {orderType === "limit" && (
          <div>
            <Label className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Limit price</Label>
            <Input
              type="number" step="0.01" value={limitPrice}
              onChange={(e) => setLimitPrice(e.target.value)}
              data-testid="order-limit-input"
              className="bg-zinc-950 border-zinc-700 rounded-sm mono mt-2 h-10"
            />
          </div>
        )}

        <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-800">
          <div className="flex justify-between px-3 py-2 text-xs">
            <span className="text-zinc-500 uppercase mono tracking-widest">Market price</span>
            <span className="mono">{quote?.price ? fmtMoney(quote.price) : "—"}</span>
          </div>
          <div className="flex justify-between px-3 py-2 text-xs">
            <span className="text-zinc-500 uppercase mono tracking-widest">Estimated total</span>
            <span className="mono font-semibold">{fmtMoney(est)}</span>
          </div>
        </div>
      </div>

      <div className="p-5 border-t border-zinc-800">
        <Button
          onClick={submit} disabled={busy || !symbol}
          data-testid="order-submit-btn"
          className={`w-full h-11 rounded-sm font-semibold ${
            side === "buy" ? "bg-[#00C805] hover:bg-[#00b004] text-black" : "bg-[#FF3B30] hover:bg-[#e63329] text-white"
          }`}
        >
          {busy ? "…" : `${side.toUpperCase()} ${qty || 0} ${symbol}`}
        </Button>
      </div>
    </div>
  );
}
